import branding from '../config/signInBranding.json';

export class PortalMfaSetupError extends Error {
    constructor(cause) {
        super("Your authenticator setup didn't finish. Please try again.");
        this.name = 'PortalMfaSetupError';
        this.cause = cause;
    }
}

export async function beginPortalMfa(auth, { friendlyName = branding.brands['the-ride-atl'].authenticatorLabel, forceChallenge = false } = {}) {
    if (!auth?.mfa) {
        throw new Error('Two-step sign-in is unavailable. Please contact your account manager.');
    }

    const { data: assurance, error: assuranceError } = await auth.mfa.getAuthenticatorAssuranceLevel();
    if (assuranceError) throw assuranceError;
    const { data: factors, error: factorsError } = await auth.mfa.listFactors();
    if (factorsError) throw factorsError;
    const verifiedFactor = (factors?.totp || []).find((factor) => factor.status === 'verified');
    if (!forceChallenge && assurance?.currentLevel === 'aal2' && verifiedFactor) {
        return { kind: 'verified' };
    }

    let factor = verifiedFactor;
    let enrollment = null;
    if (!factor) {
        const unverifiedFactors = (factors?.all || []).filter(
            (item) => item.factor_type === 'totp' && item.status === 'unverified'
        );
        try {
            for (const staleFactor of unverifiedFactors) {
                const { error } = await auth.mfa.unenroll({ factorId: staleFactor.id });
                if (error) throw error;
            }
        } catch (cleanupError) {
            throw new PortalMfaSetupError(cleanupError);
        }
        const { data, error } = await auth.mfa.enroll({
            factorType: 'totp',
            friendlyName,
        });
        if (error) throw error;
        factor = data;
        enrollment = data?.totp || null;
    }

    if (!factor?.id) {
        throw new Error('Your authenticator could not be set up. Please try again.');
    }

    const { data: challenge, error: challengeError } = await auth.mfa.challenge({
        factorId: factor.id,
    });
    if (challengeError) throw challengeError;
    if (!challenge?.id) {
        throw new Error('Your authenticator code could not be requested. Please try again.');
    }

    return {
        kind: enrollment ? 'enroll' : 'challenge',
        factorId: factor.id,
        challengeId: challenge.id,
        qrCode: enrollment?.qr_code || '',
        secret: enrollment?.secret || '',
        issuedAt: Date.now(),
    };
}

// A code window lasts about five minutes. Past that, or when the server says the window is gone,
// a fresh window is started and the person is told, rather than showing a dead-end error.
export const CODE_WINDOW_MS = 5 * 60 * 1000;
export const FRESH_CODE_NOTICE = 'That took a little too long \u2014 we started a fresh code. Enter the code your phone shows now.';

export function isChallengeGone(error) {
    if (!error) return false;
    const code = String(error.code || '');
    if (code === 'mfa_challenge_expired' || code === 'mfa_challenge_not_found') return true;
    return /challenge/i.test(String(error.message || '')) && /expired|not found|could not be found/i.test(String(error.message || ''));
}

export function isCodeWindowExpired(setup, now = Date.now()) {
    return Number.isFinite(setup?.issuedAt) && now - setup.issuedAt >= CODE_WINDOW_MS;
}

// Starts a new window on the same authenticator (the QR code and key stay valid). The old window is never reused.
export async function refreshPortalMfaChallenge(auth, setup) {
    const { data: challenge, error } = await auth.mfa.challenge({ factorId: setup.factorId });
    if (error) throw error;
    if (!challenge?.id) throw new Error('Your authenticator code could not be requested. Please try again.');
    return { ...setup, challengeId: challenge.id, issuedAt: Date.now() };
}


export async function verifyPortalMfa(auth, { factorId, challengeId, code }) {
    if (!factorId || !challengeId || !String(code || '').trim()) {
        throw new Error('Enter the current code from your authenticator app.');
    }

    const { error } = await auth.mfa.verify({
        factorId,
        challengeId,
        code: String(code).trim(),
    });
    if (error) throw error;

    const { data: assurance, error: assuranceError } = await auth.mfa.getAuthenticatorAssuranceLevel();
    if (assuranceError) throw assuranceError;
    if (assurance?.currentLevel !== 'aal2') {
        throw new Error('Your account stays locked until two-step sign-in is complete.');
    }
}

// Verifies a code. If the window has run out (or the server says it is gone) nothing is consumed:
// a fresh window is started and returned as `refreshed` so the screen can show FRESH_CODE_NOTICE.
export async function verifyPortalMfaOrRefresh(auth, setup, code) {
    if (isCodeWindowExpired(setup)) return { refreshed: await refreshPortalMfaChallenge(auth, setup) };
    try {
        await verifyPortalMfa(auth, { factorId: setup?.factorId, challengeId: setup?.challengeId, code });
        return { refreshed: null };
    } catch (failure) {
        if (isChallengeGone(failure)) return { refreshed: await refreshPortalMfaChallenge(auth, setup) };
        throw failure;
    }
}
